import { calculateAttorneyFee } from "@/lib/utils/attorneyFeeUtils";
import { AttorneyFeeSelect } from "@/components/features/matter/AttorneyFeeSelect/AttorneyFeeSelect";
import { SelectedInsuranceCard } from "@/components/features/matter/settlements/SelectedInsuranceCard";
import { SelectMatterInsuranceFormField } from "@/components/features/matter/settlements/SelectMatterInsuranceFormField";
import {
  ActionConfirmationModal,
  AppModal,
  Button,
  DateFormField,
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormSubmitButton,
  SimpleSelectFormField,
  SwitchFormField,
} from "@/components/ui";
import { FormLoadingOverlay } from "@/components/ui/Form/FormLoadingOverlay";
import { CheckboxFormField } from "@/components/ui/FormFields/CheckboxFormField/CheckboxFormField";
import { CurrencyFormField } from "@/components/ui/FormFields/CurrencyFormField/CurrencyFormField";
import { FormCancelButton } from "@/components/ui/FormFields/FormCancelButton/FormCancelButton";
import { TooltipWrapper } from "@/components/ui/shadcn/Tooltip/TooltipWrapper";
import { useUser } from "@/components/UserProvider";
import { retrieveMatterQueryOptions } from "@/lib/data/react-query/matters/matter";
import {
  useCreateMatterSettlementCounter,
  useDeleteMatterSettlementCounter,
  useUpdateMatterSettlementCounter,
} from "@/lib/data/react-query/matters/matter-settlement-counter";
import {
  MatterInsuranceSchema,
  MatterSettlementCounterSchema,
} from "@/lib/data/schemas/matters/MatterSchema";
import { MATTER_INSURANCE_COVERAGE_TYPE_OPTIONS } from "@/lib/data/static/matters/insuranceCoverageStatics";
import { MATTER_SETTLEMENT_MONEY_STATUS_OPTIONS } from "@/lib/data/static/matters/settlementStatics";
import { MATTER_DEFAULT_ATTORNEY_FEE_OPTIONS } from "@/lib/data/static/matterStatics";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import {
  SLIDEOVER_DESCRIPTION,
  SLIDEOVER_TITLE,
} from "@/lib/data/static/slideoverStatics";
import type { MatterInsurance, MatterSettlementCounter } from "@/lib/types";
import { cn } from "@/lib/utils/cn";
import { getInsuranceContactConnection } from "@/lib/utils/connections/insurance-contact-connections";
import { getMatterContactConnections } from "@/lib/utils/matters/matter-contact-connection-party-roles";
import { mergeInitialData } from "@/lib/utils/initialDataUtils";
import { slideoverFieldsetClassName } from "@/lib/utils/slideover-classes";
import { useModals } from "@/providers/ModalProvider";
import { zodResolver } from "@hookform/resolvers/zod";
import { useSuspenseQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { isNil, omitBy } from "lodash";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import type { z } from "zod";

interface AddOrEditSettlementCounterModalProps {
  title: string;
  description?: string;
  open: boolean;
  setOpen: (open: boolean) => void;
  editing?: boolean;
  matterId: string;
  settlementCounterId?: string;
  selectedInsurance?: MatterInsurance;
  coverageType?: string;
  initialData?: Partial<MatterSettlementCounter>;
}

const settlementCounterFormSchema = MatterSettlementCounterSchema.omit({
  connections: true,
  connectionsNextCursor: true,
  dateCreated: true,
  dateModified: true,
  matterId: true,
  id: true,
}).extend({
  insuranceId: MatterInsuranceSchema.shape.id
    .optional()
    .refine((value) => value, {
      message: "Insurance is required",
    }),
});

const defaultFormValues = {
  insuranceId: undefined,
  policyLimit: false,
  amount: null,
  coverageType: undefined,
  dateSent: dayjs().format("YYYY-MM-DD"),
  accepted: false,
  acceptedDate: "",
  status: "Not Received",
  acceptedDetails: undefined,
  attorneyFeePercentage: null,
  attorneyFeeAmount: null,
};

export const AddOrEditSettlementCounterModal = ({
  title,
  description,
  open,
  setOpen,
  editing,
  matterId,
  settlementCounterId,
  selectedInsurance,
  coverageType,
  initialData,
}: AddOrEditSettlementCounterModalProps) => {
  const { updateSlideoverPayload } = useModals();
  const { hasActionPermission } = useUser();
  const [isDeleting, setIsDeleting] = useState(false);
  const [closeAfterSave, setCloseAfterSave] = useState(false);
  const hasUpdatePermission = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterSettlementCounter,
    ACTION_PERMISSION_VALUES.update,
  );
  const hasCreatePermission = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterSettlementCounter,
    ACTION_PERMISSION_VALUES.create,
  );
  const canEditSettlement = editing ? hasUpdatePermission : hasCreatePermission;

  const { data: matterData } = useSuspenseQuery(
    retrieveMatterQueryOptions(matterId),
  );
  const matter = matterData.data;

  const selectedCounter = settlementCounterId
    ? matter.settlementCounters.find(
        (counter) => counter.id === settlementCounterId,
      )
    : undefined;

  const counterInitialValues = mergeInitialData(selectedCounter, initialData);
  const resolvedCounterCoverageType =
    counterInitialValues?.coverageType ?? coverageType;

  const settlementCounterForm = useForm<
    z.infer<typeof settlementCounterFormSchema>
  >({
    resolver: zodResolver(settlementCounterFormSchema),
    values: (editing && selectedCounter
      ? {
          ...defaultFormValues,
          ...(counterInitialValues ?? {}),
          coverageType: resolvedCounterCoverageType,
        }
      : {
          ...defaultFormValues,
          insuranceId: selectedInsurance?.id,
          attorneyFeePercentage: matter.attorneyFee ?? null,
          coverageType: coverageType,
        }) as z.infer<typeof settlementCounterFormSchema>,
    mode: "onSubmit",
    reValidateMode: "onSubmit",
  });

  const createSettlementCounter = useCreateMatterSettlementCounter();
  const updateSettlementCounter = useUpdateMatterSettlementCounter();
  const deleteSettlementCounter = useDeleteMatterSettlementCounter();

  const isFormLoading =
    createSettlementCounter.isPending ||
    updateSettlementCounter.isPending ||
    deleteSettlementCounter.isPending;

  const matterSettlementAmountFormValue = settlementCounterForm.watch("amount");
  const matterSettlementAcceptedFormValue =
    settlementCounterForm.watch("accepted");
  const insuranceIdFormValue = settlementCounterForm.watch("insuranceId");
  const selectedInsuranceId = insuranceIdFormValue ?? selectedInsurance?.id;
  const selectedInsuranceRecord =
    selectedInsuranceId !== undefined
      ? matter.insurances.find(
          (insurance) => insurance.id === selectedInsuranceId,
        )
      : undefined;
  const insuredConnection = getInsuranceContactConnection(
    selectedInsuranceRecord,
    "Insured",
  );
  const clientConnection = getMatterContactConnections(matter).find(
    (connection) => connection.label === "Client",
  );
  const isFirstPartyInsurance =
    !!insuredConnection?.targetObjectId &&
    insuredConnection.targetObjectId === clientConnection?.contactId;
  const isAutoInsurance = selectedInsuranceRecord?.type === "Auto";
  const shouldRequireCoverageType = isAutoInsurance && isFirstPartyInsurance;

  const onSubmit = async (
    data: z.infer<typeof settlementCounterFormSchema>,
  ) => {
    if (shouldRequireCoverageType && !data.coverageType) {
      settlementCounterForm.setError("coverageType", {
        type: "manual",
        message: "Coverage type is required for first-party auto settlements.",
      });
      return;
    }
    if (data.accepted && !data.acceptedDate) {
      settlementCounterForm.setError("acceptedDate", {
        type: "manual",
        message: "Accepted date is required when counter is accepted.",
      });
      return;
    }
    const { acceptedDate, amount, ...formData } = data;
    const { attorneyFeeAmount, ...counterFormData } = formData;
    void attorneyFeeAmount;
    const counterPayload = omitBy(
      {
        ...counterFormData,
        ...(amount == null ? {} : { amount }),
        ...(data.accepted && acceptedDate ? { acceptedDate } : {}),
      },
      isNil,
    );
    counterPayload.coverageType =
      selectedCounter?.coverageType ??
      coverageType ??
      data.coverageType ??
      null;
    let resData: MatterSettlementCounter | undefined;

    if (editing && selectedCounter) {
      const res = await updateSettlementCounter.mutateAsync({
        ...counterPayload,
        matterId: matter.id,
        dateModified: selectedCounter.dateModified,
        id: selectedCounter.id,
      });
      resData = res.data;
    } else {
      const res = await createSettlementCounter.mutateAsync({
        ...counterPayload,
        matterId: matter.id,
      });
      resData = res.data;
      updateSlideoverPayload("settlement-counter", {
        title: SLIDEOVER_TITLE.SETTLEMENT_COUNTER.UPDATE,
        description: SLIDEOVER_DESCRIPTION.SETTLEMENT_COUNTER.UPDATE,
        editing: true,
        settlementCounterId: res.data.id,
      });
    }

    if (closeAfterSave) {
      setOpen(false);
      return;
    }

    const settlement = omitBy(resData, isNil);
    settlementCounterForm.reset({
      ...defaultFormValues,
      ...settlement,
    });
  };

  const handleDelete = () => {
    if (!selectedCounter) return;
    deleteSettlementCounter.mutate({
      matterId: matter.id,
      id: selectedCounter.id,
    });
    setOpen(false);
  };

  useEffect(() => {
    if (!open) {
      settlementCounterForm.reset();
      setCloseAfterSave(false);
    }
  }, [open, settlementCounterForm]);

  return (
    <>
      <Form {...settlementCounterForm}>
        <AppModal
          open={open}
          onOpenChange={setOpen}
          title={title}
          description={description}
          size="4xl"
          loading={isFormLoading}
          isDirty={canEditSettlement && settlementCounterForm.formState.isDirty}
          preview={
            selectedInsuranceRecord ? (
              <SelectedInsuranceCard
                insurance={selectedInsuranceRecord}
                isFirstPartyInsurance={isFirstPartyInsurance}
              />
            ) : (
              <div />
            )
          }
          previewLabel={null}
        >
          <form
            className="relative flex grow flex-col overflow-hidden"
            onSubmit={async (e) => {
              e.preventDefault();
              e.stopPropagation();
              await settlementCounterForm.handleSubmit(onSubmit)();
            }}
          >
            {isFormLoading && <FormLoadingOverlay />}
            <div className="overflow-y-auto">
              <fieldset
                className={cn(
                  slideoverFieldsetClassName,
                  "my-0 grid-cols-1 p-4 sm:p-5 md:grid-cols-2",
                )}
                disabled={
                  editing
                    ? !hasActionPermission(
                        ACTION_PERMISSION_KEYS.matterSettlementCounter,
                        ACTION_PERMISSION_VALUES.update,
                      )
                    : false
                }
              >
                <div className="col-span-full grid grid-cols-1 gap-4 md:grid-cols-2">
                  <SelectMatterInsuranceFormField
                    matter={matter}
                    label="Insurance*"
                    register={settlementCounterForm.register("insuranceId")}
                  />
                  <DateFormField
                    register={settlementCounterForm.register("dateSent")}
                    name="dateSent"
                    label="Date sent*"
                  />
                </div>
                <SimpleSelectFormField
                  label="Coverage type*"
                  placeholder="Coverage type"
                  register={settlementCounterForm.register("coverageType")}
                  items={MATTER_INSURANCE_COVERAGE_TYPE_OPTIONS}
                  formatItemLabel={(item) => item}
                  disabled
                />
                <CheckboxFormField
                  register={settlementCounterForm.register("policyLimit")}
                  label="Policy limit"
                />
                <CurrencyFormField
                  register={settlementCounterForm.register("amount")}
                  label="Counter amount"
                  onChange={(value) => {
                    const amount = Number(value);
                    if (amount) {
                      settlementCounterForm.setValue(
                        "attorneyFeeAmount",
                        calculateAttorneyFee(
                          amount,
                          settlementCounterForm.watch("attorneyFeePercentage"),
                        ),
                      );
                    }
                  }}
                />
                <FormField
                  control={settlementCounterForm.control}
                  name="attorneyFeePercentage"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex justify-between">
                        <FormLabel>Attorney fee (%)</FormLabel>
                        <FormMessage />
                      </div>
                      <AttorneyFeeSelect
                        placeholder="Select or enter an attorney fee"
                        value={field.value?.toString() ?? ""}
                        onChange={(value) => {
                          settlementCounterForm.setValue(
                            "attorneyFeePercentage",
                            Number(value),
                            {
                              shouldDirty: true,
                            },
                          );
                          if (
                            Number(matterSettlementAmountFormValue) &&
                            Number(matterSettlementAmountFormValue) > 0
                          ) {
                            settlementCounterForm.setValue(
                              "attorneyFeeAmount",
                              Number(
                                calculateAttorneyFee(
                                  matterSettlementAmountFormValue ?? 0,
                                  value,
                                ).toFixed(2),
                              ),
                              {
                                shouldDirty: true,
                              },
                            );
                          }
                        }}
                        options={MATTER_DEFAULT_ATTORNEY_FEE_OPTIONS}
                        createMessage="Add an attorney fee"
                        emptyMessage="No attorney fee found"
                        label=""
                      />
                    </FormItem>
                  )}
                />
                <SimpleSelectFormField
                  label="Money status"
                  placeholder="Select money status"
                  register={settlementCounterForm.register("status")}
                  items={MATTER_SETTLEMENT_MONEY_STATUS_OPTIONS}
                  formatItemLabel={(item) => item}
                  clearable
                />
                <TooltipWrapper
                  message={
                    !matterSettlementAmountFormValue
                      ? "Enter a counter amount first."
                      : undefined
                  }
                >
                  <div>
                    <CurrencyFormField
                      register={settlementCounterForm.register(
                        "attorneyFeeAmount",
                      )}
                      label="Attorney fee amount"
                      fixedDecimalScale
                      disabled={!matterSettlementAmountFormValue}
                      onChange={(value) => {
                        if (value) {
                          settlementCounterForm.setValue(
                            "attorneyFeePercentage",
                            Math.round(
                              (Number(value) /
                                (settlementCounterForm.watch("amount") ?? 0)) *
                                100 *
                                100,
                            ) / 100,
                            {
                              shouldDirty: true,
                            },
                          );
                        }
                        settlementCounterForm.setValue(
                          "attorneyFeeAmount",
                          Number(value),
                          {
                            shouldDirty: true,
                          },
                        );
                      }}
                    />
                  </div>
                </TooltipWrapper>
                <SwitchFormField
                  register={settlementCounterForm.register("accepted")}
                  label={<span>Accepted?</span>}
                />
                {matterSettlementAcceptedFormValue && (
                  <>
                    <div className="col-span-full" />
                    <DateFormField
                      register={settlementCounterForm.register("acceptedDate")}
                      name="acceptedDate"
                      label="Accepted date"
                    />
                    <div />
                  </>
                )}
              </fieldset>
            </div>
            <div className="flex grow" />
            <div className="border-border bg-card shrink-0 border-t px-5 py-3">
              <div className="flex w-full items-center justify-between gap-2">
                {editing &&
                  selectedCounter &&
                  hasActionPermission(
                    ACTION_PERMISSION_KEYS.matterSettlementCounter,
                    ACTION_PERMISSION_VALUES.delete,
                  ) && (
                    <Button
                      type="button"
                      variant="destructive"
                      onClick={() => {
                        setIsDeleting(true);
                      }}
                    >
                      Delete
                    </Button>
                  )}
                <div className="flex grow justify-end gap-2">
                  <FormCancelButton
                    setOpen={setOpen}
                    isDirty={settlementCounterForm.formState.isDirty}
                  />
                  <FormSubmitButton
                    title={editing ? "Save" : "Add counter"}
                    loading={isFormLoading}
                    overrideDisabled={Boolean(initialData)}
                    submitFn={() => {
                      setCloseAfterSave(true);
                    }}
                  />
                </div>
              </div>
            </div>
          </form>
        </AppModal>
      </Form>

      <ActionConfirmationModal
        open={isDeleting}
        setOpen={setIsDeleting}
        title="Delete settlement counter"
        description="Are you sure you want to delete this settlement counter?"
        buttonText="Delete"
        afterActionFn={handleDelete}
        actionVariant="destructive"
      />
    </>
  );
};
